// deploymentSitesTable.tsx
//
// (c) NCR Voyix Corporation 2025. All rights reserved.

import React from 'react'
import { GridColDef } from '@mui/x-data-grid-pro'
import { connect } from 'react-redux'
import { AppThunkDispatch, RootState } from '../../../redux'
import Table from '../../../components/Table'
import { getSitesData } from '../../../redux/sites/thunks'
import { useCancellableEffect } from '../../../hooks/useCancellableEffect'
import { setDeploymentSitesIds } from '../../../redux/deploymentLite/slice'

function mapDispatchToProps(dispatch: AppThunkDispatch) {
  return {
    dispatchGetSitesData: (...args: Parameters<typeof getSitesData>) => dispatch(getSitesData(...args)),
    dispatchSetDeploymentSitesIds: (...args: Parameters<typeof setDeploymentSitesIds>) => dispatch(setDeploymentSitesIds(...args))
  }
}

function mapStateToProps(state: RootState) {
  return {
    user: state.user,
    company: state.company,
    sites: state.sites.sites,
    requestSitesState: state.sites.requestSitesState
  }
}

type DispatchProps = ReturnType<typeof mapDispatchToProps>
type StateProps = ReturnType<typeof mapStateToProps>
type DeploymentSitesTableProps = DispatchProps & StateProps

function DeploymentSitesTable(
  {
    user,
    company,
    sites,
    requestSitesState,
    dispatchGetSitesData,
    dispatchSetDeploymentSitesIds
  }: DeploymentSitesTableProps
): JSX.Element {
  useCancellableEffect((cancelToken) => {
    if (user.userId && user.sessionId && user.dataCenter && company.selectedCompany?.id) {
      dispatchGetSitesData({
        userId: user.userId,
        sessionId: user.sessionId,
        dataCenter: user.dataCenter,
        companyId: company.selectedCompany.id,
        cancelToken
      })
    }
  }, [company.selectedCompany?.id])

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'Site ID',
      flex: 1,
      minWidth: 100,
      maxWidth: 100,
      width: 100,
      headerAlign: 'center'
    },
    {
      field: 'siteName',
      headerName: 'Site Name',
      flex: 1,
      headerAlign: 'center'
    }
  ]

  const rows = Object.values(sites).map((site) => ({
    id: site.id,
    siteName: site.name
  }))

  return (
    <Table
      width="100%"
      columns={columns}
      toolbar
      checkboxSelection
      disableSelectionOnClick
      rows={requestSitesState === 'pending' ? [] : rows}
      loading={requestSitesState === 'pending'}
      useGlobalPageSize
      onSelectionModelChange={(siteIds) => {
        dispatchSetDeploymentSitesIds(siteIds as string[])
      }}
    />
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(DeploymentSitesTable)
